/**
 * What a bulk save is about to do, said in a director's words.
 *
 * The Bulk edit table collects pending edits as a map of game id → changed
 * fields. Before anything is written, the confirmation has to describe that
 * map: grouped by the kind of change, with the before and the after on every
 * line, and the game number and division leading each one.
 *
 * Nothing here decides anything — it only reads the pending edits against the
 * rows they came from. The problems a save would create come from the server,
 * which runs the same invariants the publish check runs.
 */
import { formatCalendarDate, formatTime } from '@/lib/dates';

export type SummaryGame = {
  id: string;
  game_number?: number | null;
  division?: string | null;
  pool_name?: string | null;
  bracket_label?: string | null;
  scheduled_date?: string | null;
  scheduled_start_time?: string | null;
  court_id?: string | null;
  team_a_registration_id?: string | null;
  team_b_registration_id?: string | null;
  team_a_name?: string | null;
  team_b_name?: string | null;
};

export type SummaryEdit = {
  scheduled_date?: string | null;
  scheduled_start_time?: string | null;
  court_id?: string | null;
  team_a_registration_id?: string | null;
  team_b_registration_id?: string | null;
  delete?: boolean;
};

export type ChangeRow = { id: string; game: string; before: string; after: string };
export type ChangeGroup = { kind: string; title: string; rows: ChangeRow[] };

type Lookups = {
  /** court id → court name */
  courtName: (id: string | null | undefined) => string;
  /** registration id → team name */
  teamName: (id: string | null | undefined) => string;
};

/** "16u Game 11" — number first, division in front, per item 1. */
export function gameLabel(g: SummaryGame): string {
  const div = g.division ? `${String(g.division).trim()} ` : '';
  return g.game_number != null ? `${div}Game ${g.game_number}` : `${div}${g.bracket_label ?? 'a game'}`.trim();
}

/** "Court D Sat 1:15pm", "Sat 1:15pm", "no court or time". */
export function whereLabel(
  court: string | null | undefined,
  date: string | null | undefined,
  time: string | null | undefined,
): string {
  const when = [
    date ? formatCalendarDate(date, 'EEE d MMM') : '',
    time ? formatTime(time) : '',
  ].filter(Boolean).join(' ');
  const text = [court, when].filter(Boolean).join(' ');
  return text || 'no court or time';
}

const has = (e: SummaryEdit, k: keyof SummaryEdit) =>
  Object.prototype.hasOwnProperty.call(e, k);

/**
 * Group the pending edits: deletions, moves (court, date or time), and matchup
 * changes (either team). A single game can appear in both a move and a matchup
 * group — they are two different things a director asked for.
 */
export function summarizeEdits(
  games: SummaryGame[],
  edits: Record<string, SummaryEdit>,
  look: Lookups,
): ChangeGroup[] {
  const byId = new Map(games.map((g) => [g.id, g]));
  const moved: ChangeRow[] = [];
  const matchup: ChangeRow[] = [];
  const deleted: ChangeRow[] = [];

  for (const [id, e] of Object.entries(edits)) {
    const g = byId.get(id);
    if (!g) continue;
    const label = gameLabel(g);

    if (e.delete) {
      deleted.push({
        id,
        game: label,
        before: whereLabel(look.courtName(g.court_id), g.scheduled_date, g.scheduled_start_time),
        after: 'deleted',
      });
      continue;
    }

    const movedFields = has(e, 'court_id') || has(e, 'scheduled_date') || has(e, 'scheduled_start_time');
    if (movedFields) {
      const court = has(e, 'court_id') ? e.court_id : g.court_id;
      const date = has(e, 'scheduled_date') ? e.scheduled_date : g.scheduled_date;
      const time = has(e, 'scheduled_start_time') ? e.scheduled_start_time : g.scheduled_start_time;
      moved.push({
        id,
        game: label,
        before: whereLabel(look.courtName(g.court_id), g.scheduled_date, g.scheduled_start_time),
        after: whereLabel(look.courtName(court), date, time),
      });
    }

    const teamFields = has(e, 'team_a_registration_id') || has(e, 'team_b_registration_id');
    if (teamFields) {
      const a = has(e, 'team_a_registration_id') ? e.team_a_registration_id : g.team_a_registration_id;
      const b = has(e, 'team_b_registration_id') ? e.team_b_registration_id : g.team_b_registration_id;
      matchup.push({
        id,
        game: label,
        before: `${g.team_a_name || 'TBD'} vs ${g.team_b_name || 'TBD'}`,
        after: `${look.teamName(a) || 'TBD'} vs ${look.teamName(b) || 'TBD'}`,
      });
    }
  }

  const n = (c: number, one: string, many: string) => (c === 1 ? one : many);
  const groups: ChangeGroup[] = [];
  if (moved.length) {
    groups.push({ kind: 'moved', title: `Moving ${moved.length} ${n(moved.length, 'game', 'games')}`, rows: moved });
  }
  if (matchup.length) {
    groups.push({
      kind: 'matchup',
      title: `Changing ${matchup.length} ${n(matchup.length, 'matchup', 'matchups')}`,
      rows: matchup,
    });
  }
  if (deleted.length) {
    groups.push({
      kind: 'deleted',
      title: `Deleting ${deleted.length} ${n(deleted.length, 'game', 'games')}`,
      rows: deleted,
    });
  }
  return groups;
}

/** The first `limit` rows across all groups, and how many are left over. */
export function firstRows(groups: ChangeGroup[], limit = 12): { shown: ChangeGroup[]; hidden: number } {
  let budget = limit;
  const shown: ChangeGroup[] = [];
  let hidden = 0;
  for (const g of groups) {
    if (budget <= 0) { hidden += g.rows.length; continue; }
    const take = g.rows.slice(0, budget);
    hidden += g.rows.length - take.length;
    budget -= take.length;
    shown.push({ ...g, rows: take });
  }
  return { shown, hidden };
}
